// subtasks/hooks/useSubTaskListController.ts
import { format } from 'date-fns';
import { useAppContext } from '../../../AppContext';
import { useTaskListActions, useTaskListSelectionState } from '../../context/TaskListContext';
import { parseDeepLinkHash } from '../../deepLinkUtils';

import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { Cancelable, EventInfo, one } from 'devextreme/events';
import dxDataGrid, {
    ColumnCellTemplateData,
    DataRowTemplateData,
    RowPreparedEvent,
    SelectionChangedEvent
} from 'devextreme/ui/data_grid';
import dxSortable from 'devextreme/ui/sortable';
import dxTooltip from 'devextreme/ui/tooltip';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getSteps } from '../../../../../api/v2/PhasesApi';
import { getStep, updateStep } from '../../../../../api/v2/StepsApi';
import { confirm } from 'devextreme/ui/dialog';
import { deleteDefaultTask } from '../../../../../api/v2/TasklistsApi';

import { PhaseAction } from '../../../../../dtos/PhaseAction';
import { Modules } from '../../../../../dtos/Modules';
import { PhaseDto } from '../../../../../dtos/PhaseDto';
import { PhaseSettings } from '../../../../../dtos/PhaseSettings';
import { PhaseStatus } from '../../../../../dtos/PhaseStatus';
import { PhaseSubmissionDto } from '../../../../../dtos/PhaseSubmissionDto';
import { PhaseWorkflowType } from '../../../../../dtos/PhaseWorkflowType';
import { StepDto } from '../../../../../dtos/StepDto';
import { StepStatus } from '../../../../../dtos/StepStatus';

import { isTemplatePhase } from '../../../Types';
import { getStepStatusInfo, getUserFullName, checkStepPermission, checkPhasePermission, getEffectiveStepType, isHybridParentType, stepToPhaseWorkflowType, isFinalPhaseStatus } from '../../../Utils';
import { PermissionType } from '../../../../../dtos/PermissionType';
import { ISubTaskListProps } from '../SubTaskList';
import { ISubTaskListViewProps } from '../components/SubTaskListView';
import { updateDefaultSubtask } from '../../../../../api/v2/TasklistsApi';
import { mapStepDtoToDefaultStep } from '../../tasks/adapters/templateMappers';
import { cycleVocabulary, cyclesNewestFirst } from "../../tasks/taskCycle";
import { useTaskCycleControlController } from "../../tasks/hooks/useTaskCycleControlController";
import { downloadPhaseFiles, showsDownloadAll } from "../../tasks/downloadPhaseFiles";
import { splitConfiguredFiles } from "../../stepFiles";

// Counts selections across every task on the page, so a subtask read that comes back after the
// user has already clicked another one knows to drop what it read (ticket 1066).
let selectionCount = 0;

export function useSubTaskListController({
    getPhaseData,
    hidePhaseSummary,
    showPhaseRequestResponseSummary,
    hidePhaseRequestResponseSummary,
    deepLinkHash,
    phaseIsSelected = false,
    onAdding,
    onLoading,
    onLoaded,
    onTaskSelected,
    onVisibilityChange,
    onCollapsedChange,
    mode,
    flags,
    grantData,
    nameShownExternally,
    externalPhase
}: ISubTaskListProps): ISubTaskListViewProps {
    const app = useAppContext();
    const taskListActions = useTaskListActions();
    const { tasklistMode, selectedPhase: contextSelectedPhase, selectedStep: contextSelectedStep } = useTaskListSelectionState();

    // Use mode from props if provided, otherwise use from context
    const currentMode = mode ?? tasklistMode;

    const deepLinkToTask = useRef<boolean>(false);
    const deepLinkProcessed = useRef<boolean>(false);
    const gridRef = useRef<dxDataGrid<StepDto, string> | null>(null);
    // Set while this task is the one being selected in, so a broadcast clear passes its own grid by.
    const skipOwnGridClear = useRef<boolean>(false);
    const scrollToBottomOnNextRefresh = useRef<boolean>(false);


    const [phase, setPhase] = useState<PhaseDto | null>(null);
    const phaseRef = useRef<PhaseDto | null>(null);
    const phaseSelected = useRef<boolean>(phaseIsSelected);
    const [stepCount, setStepCount] = useState<number>(0);
    // The subtasks currently in view (already permission-filtered), across every cycle. Null
    // until the first load finishes.
    const [visibleSteps, setVisibleSteps] = useState<StepDto[] | null>(null);
    // The newest cycle's share of visibleSteps. The toolbar's task status indicator is derived
    // from these for Step-style tasks, which have no task-level status - a prior cycle's steps
    // must not feed it - and the subtask count and step limit read this cycle only. Null until
    // the first load finishes, so the indicator stays hidden rather than reading "Not Started"
    // for a beat on every task.
    const [currentCycleSteps, setCurrentCycleSteps] = useState<StepDto[] | null>(null);
    // The newest cycle count as loaded, for the recounts below to fall back on.
    const loadedCurrentCycleCount = useRef<number>(0);
    const stepCountRef = useRef<number>(0); // Synchronous ref to track count immediately
    const [hasLoaded, setHasLoaded] = useState<boolean>(false);

    // Collapse is per task and lasts for the session only - nothing is persisted, so a reload
    // returns every task to the status-derived default below.
    const [collapsed, setCollapsed] = useState<boolean>(false);
    // Finished tasks open collapsed so the list leads with what still needs work. Applied once,
    // when the phase first arrives: after that the user's own toggling wins, or a task would
    // spring shut again every time its phase reloaded.
    const collapseDefaultApplied = useRef<boolean>(false);

    useEffect(() => {
        if (!phase || collapseDefaultApplied.current) return;
        collapseDefaultApplied.current = true;
        if (isFinalPhaseStatus(phase.status)) setCollapsed(true);
    }, [phase]);

    useEffect(() => {
        onCollapsedChange?.(collapsed);
    }, [collapsed, onCollapsedChange]);

    const toggleCollapsed = useCallback(() => {
        // Any manual toggle counts as the user taking over, even if the phase has not loaded yet.
        collapseDefaultApplied.current = true;
        setCollapsed(prev => !prev);
    }, []);

    // Selecting a subtask from outside (deep link, summary view) has to open the task holding it.
    // Keyed on the selection changing, not on `collapsed` - watching that re-expanded a task the
    // moment the user collapsed it while one of its subtasks was selected.
    const autoExpandedForStepId = useRef<string | null>(null);

    useEffect(() => {
        if (!contextSelectedStep || !phase) return;
        if (autoExpandedForStepId.current === contextSelectedStep.id) return;

        autoExpandedForStepId.current = contextSelectedStep.id;
        const stepIsInThisTask = phase.phaseSubmissions?.some(s => s.id === contextSelectedStep.phaseSubmissionId);
        if (stepIsInThisTask) setCollapsed(false);
    }, [phase, contextSelectedStep]);


    // Sync local phase with context when it changes (for same phase ID)
    useEffect(() => {
        if (contextSelectedPhase && phase && contextSelectedPhase.id === phase.id) {
            setPhase(contextSelectedPhase);
            phaseRef.current = contextSelectedPhase;
        }
    }, [contextSelectedPhase, phase?.id]);


    // Reset deep link processed flag when hash changes
    useEffect(() => {
        deepLinkProcessed.current = false;
    }, [deepLinkHash]);

    useEffect(() => {
        // If phase loaded after deep link, open summary
        if (phase && deepLinkToTask.current) {
            deepLinkToTask.current = false;
            onClickShowRequestResponseSummary();
        }
    }, [phase]);

    const dataSource = useMemo(
        () =>
            new DataSource<StepDto, string>({
                paginate: false,
                requireTotalCount: true,
                store: new CustomStore<StepDto, string>({
                    key: 'id',

                    load: async () => {
                        // Paired with the onLoaded in the finally below: the tasks pane counts loads
                        // in flight to drive its load panel, so a load that threw part way through
                        // would leave the count stuck above zero and the panel up for good.
                        onLoading?.();
                        try {
                            const phaseData = await getPhaseData();

                            // If no phase data exists (e.g., deleted template with no grant instance), skip rendering
                            if (!phaseData) {
                                return [];
                            }

                            let steps: StepDto[] = [];

                            // GRANT/RUNTIME path. Every cycle is listed, newest first, so a prior
                            // submission's subtasks sit under the current one's rather than behind
                            // a picker (ticket 1069). The grid groups them by cycle.
                            const submissions = cyclesNewestFirst(phaseData);
                            if (submissions.length) {
                                const results = await Promise.all(submissions.map(s => getSteps(s.id)));
                                for (const result of results) {
                                    if (result?.error) app.errorToast(result.error.message);
                                    else steps.push(...(result?.data ?? []));
                                }
                            }
                            // TEMPLATE path: use steps on the phase
                            else if (isTemplatePhase(phaseData)) {
                                steps = phaseData.steps; // fully typed now
                            }

                            // Filter steps based on View permission from config
                            // Skip filter for template mode (all steps visible when editing templates)
                            if (currentMode !== "templates") {
                                steps = steps.filter(step => checkStepPermission(step, app, PermissionType.View, true, phaseData, grantData?.id));
                            }

                            const newestId = submissions[0]?.id;
                            const current = newestId ? steps.filter(s => s.phaseSubmissionId === newestId) : steps;

                            // (deep-link logic can stay as-is; it'll only match if your IDs are GUIDs)
                            setPhase(phaseData);
                            phaseRef.current = phaseData;
                            stepCountRef.current = current.length;
                            loadedCurrentCycleCount.current = current.length;
                            setStepCount(current.length);
                            setVisibleSteps(steps);
                            setCurrentCycleSteps(current);
                            setHasLoaded(true);

                            return steps;
                        } finally {
                            onLoaded?.();
                        }
                    },

                    update: async (key, values) => {
                        const step = currentMode !== "templates" ? await updateStep(key, values) : await updateDefaultSubtask(Number(key), mapStepDtoToDefaultStep(values));
                        if (step?.error != null) app.errorToast(step.error.message);
                        return step?.data as StepDto;
                    },

                    byKey: async (key) => {
                        const step = await getStep(key);
                        if (step?.error != null) {
                            app.errorToast(`Subtask with key ${key} not found.`);
                        }
                        return step?.data as StepDto;
                    },
                }),
            }),
        [app, deepLinkHash, getPhaseData, onLoaded, onLoading, grantData]
    );

    // New subtasks always join the newest cycle: a prior cycle takes no new subtasks, whichever
    // one the open subtask belongs to.
    const cycles = cyclesNewestFirst(phase);
    const submission: PhaseSubmissionDto | null = cycles[0] ?? null;
    // Grouped by cycle once there is more than one to show.
    const groupedByCycle = currentMode !== "templates" && cycles.length > 1;
    const phaseSettings: PhaseSettings | null | undefined = phase?.defaultPhase?.settings;

    // The count shown on the task and checked against the step limit is the newest cycle's
    // subtasks only, as loaded - not the grid's visible rows, which include group rows and prior
    // cycles and drop to nothing while a cycle group is folded.
    const countCurrentCycleRows = useCallback(() => loadedCurrentCycleCount.current, []);

    // One way to re-read the rows. The grid's own refresh repaints every row - with
    // repaintChangesOnly it decides a row changed by its VISIBLE columns, so a reload that only
    // brought new files left the count beside the name at its old number. Without a grid mounted
    // the data source reloads on its own.
    const reloadRows = useCallback(async () => {
        const grid = gridRef.current?.instance();
        if (grid) await grid.refresh(false);
        else await dataSource.reload();
    }, [dataSource]);

    // Register a refresh handler per tasklist instance so cross-tasklist trigger updates
    // (and any external refreshSteps call) refresh every visible tasklist in the module,
    // not just the one with the currently-selected step.
    useEffect(() => {
        const unregister = taskListActions.registerForRefreshSteps(async () => {
            // One load, not two: reloading the data source AND refreshing the grid fetched the
            // same thing twice on every upload (#941).
            await reloadRows();

            const grid = gridRef.current?.instance();
            if (grid && scrollToBottomOnNextRefresh.current) {
                scrollToBottomOnNextRefresh.current = false;
                const scrollable = grid.getScrollable();
                setTimeout(() => {
                    if (scrollable) scrollable.scrollTo({ top: scrollable.scrollHeight() });
                }, 100);
            }
        });
        return unregister;
    }, [taskListActions, reloadRows]);

    // Take the page's newer copy of this phase when it offers one. A status rule can finish a task
    // other than the one being worked in - a DD document completes SD - and DC holds one tasklist
    // context per task, so the refresh that follows an upload never reaches the other tasklists.
    // The page reloads every phase in the module for its headers anyway, so this adopts that result
    // instead of fetching again.
    // Keyed on the phase id, not the phase: a local reload replaces the object without changing
    // the id, and re-running here would put the page's older copy back over it.
    //
    // The rows are reloaded only when the task's own status changed, because that is the case where
    // its subtasks moved with it - completing a task completes them. Reloading on every page
    // refresh would put a grid load on every task in the module for each upload.
    useEffect(() => {
        if (!externalPhase || externalPhase.id !== phaseRef.current?.id) return;

        const statusChanged = externalPhase.status !== phaseRef.current?.status;

        setPhase(externalPhase);
        phaseRef.current = externalPhase;

        if (statusChanged) void reloadRows();
    }, [externalPhase, phase?.id, reloadRows]);


    const selectStep = useCallback(async (step: StepDto, grid: dxDataGrid<StepDto, string>) => {
        phaseSelected.current = false;
        const selection = ++selectionCount;

        // Registered before the read below, not after it: a second click while the first read was
        // still out found no handler for this grid, so its row stayed highlighted alongside the
        // new one and the two tasks went on trading the selection (ticket 1066).
        taskListActions.registerForClear(() => {
            // Skipped while this grid is the one being selected in, or the row the user just
            // clicked would deselect itself.
            if (skipOwnGridClear.current) return;
            grid.clearSelection();
        });

        // Update count from grid when navigating to a step (in case user cancelled add)
        const rowCount = countCurrentCycleRows();
        stepCountRef.current = rowCount;
        setStepCount(rowCount);

        hidePhaseSummary();

        // Read the step back before handing it to the detail panel. The grid row can be a stale
        // snapshot (the task summary saves dates one field at a time and only reloads the list),
        // and the panel posts whatever it was given straight back on save - so a stale copy here
        // silently overwrites good data. Fall back to the grid row if the read fails.
        let selected = step;
        if (currentMode !== "templates" && step.id) {
            const fresh = await getStep(step.id);
            if (fresh?.data) selected = fresh.data;
        }

        // The user has clicked another subtask while this one was being read - that click owns
        // the selection now, so leave it alone.
        if (selection !== selectionCount) return;

        // Phase and step go in together. Setting the phase before the read above left a window
        // where a phase was selected and no step was, which is the state that highlights the task
        // header - so opening a subtask flashed that highlight on and straight back off
        // (ticket 1034). React batches the two into one render. The cycle follows the subtask:
        // every cycle is on the grid, so the subtask chosen says which one is in view.
        taskListActions.setSelectedPhase(phase);
        taskListActions.setSelectedSubmissionId(selected.phaseSubmissionId ?? null);
        taskListActions.setSelectedStep(selected);
    }, [phase, taskListActions, hidePhaseSummary, currentMode, countCurrentCycleRows]);

    // The selected subtask as of this render, read from a ref so the grid's handler below always
    // sees the current one rather than whatever was selected when it was created.
    const contextSelectedStepIdRef = useRef<string | null>(null);
    contextSelectedStepIdRef.current = contextSelectedStep?.id ?? null;

    const onSelectionChange = useCallback(
        (e: SelectionChangedEvent<StepDto, string>) => {
            const step = e.selectedRowsData[0];
            if (!step) return;

            // The grid raises this for a selection made in code too, even when it lands on the row
            // that was already selected - which is what keeping the grid in step with the context
            // does after every selection. Running the selection again from there wrote the subtask
            // back to the context, which started the next round (ticket 1066).
            if (step.id && step.id === contextSelectedStepIdRef.current) {
                evaluateOrdering(e);
                return;
            }

            selectStep(step, e.component);
            evaluateOrdering(e);
        }, [selectStep]);

    const addButtonOptions = useMemo(
        () => ({
            icon: 'add',
            hint: 'Add subtask',
            text: 'Add subtask',
            onClick: () => {
                // Increment immediately when Add Subtask is clicked
                const newCount = stepCountRef.current + 1;
                stepCountRef.current = newCount;
                setStepCount(newCount);

                hidePhaseRequestResponseSummary?.();

                if (currentMode !== "templates") {
                    if (!submission) return;
                }

                onAdding?.();

                phaseSelected.current = false;

                taskListActions.clearSelections();
                taskListActions.setSelectedPhase(phase);

                if (currentMode !== "templates") {
                    // Under a hybrid parent the new subtask carries its own workflow type
                    // (user-picked on the form). Seed a defaultStep shell so the form can
                    // bind the picker into its settings and the controller's effective-type
                    // resolution sees a defaultStep on the new step.
                    const parentWorkflowType = phase?.defaultPhase?.settings?.workflowType;
                    const seedDefaultStep = (parentWorkflowType === PhaseWorkflowType.PhaseHybrid ||
                                              parentWorkflowType === PhaseWorkflowType.StepHybrid)
                        ? { id: 0, settings: { workflowType: null } } as any
                        : undefined;
                    taskListActions.setSelectedStep({
                        phaseSubmissionId: submission?.id,
                        ...(seedDefaultStep ? { defaultStep: seedDefaultStep } : {})
                    } as StepDto);
                }
                else {
                    taskListActions.setSelectedStep({
                        id: "0",
                        name: "",
                        order: 0,
                        description: null,
                        defaultStep: {
                            id: 0,
                            phaseTypeId: phase?.defaultPhase.id,
                            name: "",
                            abbrev: "",
                            key: "",
                            description: null,
                            number: 0,
                            settings: {},
                            deleted: false,
                            dateCreated: new Date(),
                            dateUpdated: new Date()
                        },
                        phaseSubmissionId: submission?.id ?? "templatesubmission"
                    } as StepDto);
                }

                // Mount-time refresh handler will scroll to the new row on the next refresh
                scrollToBottomOnNextRefresh.current = true;
            },
        }),
        [phase, submission, taskListActions, onAdding, currentMode, stepCount]
    );

    const deleteButtonOptions = {
        icon: "trash",
        hint: "Delete task",
        text: "Delete task",
        type: "danger",
        onClick: async () => {
            if (!phase?.defaultPhase) return;

            const result = await confirm(
                `Are you sure you want to delete the task "${phase.name}"? This action cannot be undone if the task has no instances.`,
                "Confirm Deletion"
            );

            if (!result) return;

            app.showLoadPanel();

            const deleteResult = await deleteDefaultTask(phase.defaultPhase.moduleTypeId, phase.defaultPhase.id);

            if (deleteResult?.error) {
                app.errorToast(deleteResult.error.message);
            } else {
                // Clear all selections after deletion to return to initial state
                taskListActions.clearSelections();
                taskListActions.setSelectedPhase(null);
                taskListActions.setSelectedStep(null);
                // Refresh the entire module to remove the deleted task from phasesInfo
                await taskListActions.refreshSteps();
            }

            app.hideLoadPanel();
        },
    };

    const renderStatus = useCallback(
        (data: ColumnCellTemplateData<StepDto, string> | DataRowTemplateData<StepDto, string>) => {
            const status = data.data?.status as StepStatus;
            // Under a hybrid parent each subtask runs its own workflow, so the status label
            // must reflect the per-subtask type (e.g. "In Progress" for None subtasks rather
            // than "In Progress - NIH"). Falls back to the parent's type for non-hybrid.
            const parentType = phase?.defaultPhase?.settings?.workflowType;
            const effectiveType = (isHybridParentType(parentType) && phase?.defaultPhase && data.data?.defaultStep)
                ? stepToPhaseWorkflowType(getEffectiveStepType(phase.defaultPhase, data.data.defaultStep))
                : parentType;
            const info = getStepStatusInfo(status, data.data?.dueDate as Date, effectiveType);
            return { text: info.text, color: info.color, border: info.border };
        }, [phase]);

    const renderDueDate = useCallback(
        (data: ColumnCellTemplateData<StepDto, string> | DataRowTemplateData<StepDto, string>) => {
            const dueDate = data.data?.dueDate;
            return dueDate ? new Date(dueDate).toDateString() : '';
        }, []);

    const rowPrepared = useCallback(
        (e: RowPreparedEvent<StepDto, string>) => {
            if (e.rowType === 'data') {
                e.rowElement.style.cursor = 'pointer';
                const originalClick = e.rowElement.onclick;
                e.rowElement.onclick = function (this: GlobalEventHandlers, ev: MouseEvent) {
                    // Let go of the other tasks' rows before this one is selected, but leave the
                    // context selection alone: clearing it here blanked the detail panel for the
                    // length of the click, which read as a flash (ticket 1034). This grid keeps
                    // its own row - the grid replaces it itself.
                    skipOwnGridClear.current = true;
                    taskListActions.clearGridSelections();
                    skipOwnGridClear.current = false;
                    return originalClick?.call(this, ev);
                };
            }
        }, [taskListActions]);

    const evaluateOrdering = async (e?: EventInfo<dxDataGrid<StepDto, string>>) => {
        const thisPhase = phaseRef.current;
        if (!thisPhase) return;

        // note: removed getSelectedStep() call here to avoid triggering re-renders
        // grid will manage its own selection state

        // Reordering allowed if user has ADD permission AND phase setting allows it. Off once the
        // grid is grouped by cycle: the sortable counts data rows while the grid counts group rows
        // too, so a drop would land on the wrong subtask.
        const allowReordering = (thisPhase.phaseActions?.includes(PhaseAction.ADD) ?? false) &&
                                (thisPhase.defaultPhase?.settings?.allowSubtaskReorder !== false) &&
                                cyclesNewestFirst(thisPhase).length < 2;

        if (!allowReordering) return;

        const gridInstance = e ? e.component : gridRef.current?.instance();
        if (!gridInstance) return;

        const rowsView = gridInstance.element().querySelector('.dx-datagrid-rowsview .dx-datagrid-table') as HTMLElement | null;
        const rows = rowsView?.querySelectorAll('tbody.dx-row.dx-data-row');

        if (!rows || rows.length === 0) {
            setTimeout(() => evaluateOrdering(e), 50);
            return;
        }

        if (rowsView && !dxSortable.getInstance(rowsView)) {
            new dxSortable(rowsView, {
                filter: 'tbody.dx-row.dx-data-row',
                itemOrientation: 'vertical',
                moveItemOnDrop: true,
                onDragEnd: async (dragEndEvent) => {
                    const fromIndex = dragEndEvent.fromIndex;
                    const toIndex = dragEndEvent.toIndex;
                    if (fromIndex !== toIndex) {
                        const fromId = gridInstance.getKeyByRowIndex(fromIndex);
                        const step = gridInstance.getDataSource().items().find((x: StepDto) => x.id === fromId) as StepDto;
                        step.order = toIndex;
                        await dataSource.store().update(step.id, step);
                        await gridInstance.refresh();
                    }
                },
            });
        }
    };

    // parseDeepLinkHash imported from ../../deepLinkUtils

    const onContentReady = useCallback(
        async (e: EventInfo<dxDataGrid<StepDto, string>>) => {
            // Always update count from grid after any content change
            // This handles add (after save), delete, and any other operations
            const gridInstance = e.component;
            if (gridInstance) {
                const newCount = countCurrentCycleRows();
                // Only update if count actually changed to avoid unnecessary re-renders
                if (stepCountRef.current !== newCount) {
                    stepCountRef.current = newCount;
                    setStepCount(newCount);
                }
            }

            // Handle deep link selection (only once)
            if (deepLinkHash && !deepLinkProcessed.current && gridInstance) {
                const { taskId, subtaskId } = parseDeepLinkHash(deepLinkHash);

                if (subtaskId) {
                    // Subtask ID provided - select it in the grid
                    console.log(`Deep link selection: Looking for subtask with ID ${subtaskId}`);

                    const allRows = gridInstance.getDataSource().items() as StepDto[];
                    const targetStep = allRows.find((step: StepDto) => step.id === subtaskId);

                    console.log(`Found ${allRows.length} rows, target found: ${!!targetStep}`);

                    if (targetStep) {
                        // Mark as processed before selecting to avoid infinite loops
                        deepLinkProcessed.current = true;

                        console.log(`Selecting subtask: ${targetStep.name || subtaskId}`);

                        // Add small delay to ensure grid is fully ready
                        setTimeout(() => {
                            // Select the step programmatically
                            gridInstance.selectRows([subtaskId], false);

                            // Scroll to the selected row
                            const rowIndex = gridInstance.getRowIndexByKey(subtaskId);
                            if (rowIndex >= 0) {
                                gridInstance.navigateToRow(subtaskId);
                                console.log(`Navigated to row index ${rowIndex}`);
                            }
                        }, 150);
                    } else {
                        console.log(`Target subtask not found in grid`);
                    }
                } else if (taskId && taskId === phaseRef.current?.id) {
                    // Only task ID provided (no subtask) - show task summary
                    // Only claim this deep link if the task ID matches THIS phase
                    console.log(`Deep link selection: Task-only link detected for task ID ${taskId}, matches phase ${phaseRef.current?.id}`);
                    deepLinkProcessed.current = true;
                    deepLinkToTask.current = true;
                }
            }

            setTimeout(() => {
                evaluateOrdering(e);
            }, 50);
        },
        [dataSource, app, deepLinkHash, countCurrentCycleRows]
    );

    const onTooltipHiding = useCallback((e: Cancelable & EventInfo<dxTooltip>) => {
        // Accept a Promise for async hide (DevExtreme quirk)
        // @ts-ignore
        e.cancel = new Promise<void>((resolve) => {
            setTimeout(() => {
                const popupContainer = e.component.content().parentElement;
                if (popupContainer?.parentElement?.matches(':hover')) {
                    one(popupContainer, 'mouseleave', () => resolve());
                } else {
                    resolve();
                }
            }, 50);
        });
    }, []);

    const dataRowTemplate = (rowInfo: DataRowTemplateData<StepDto, string>) => {
        const status = renderStatus(rowInfo); // {text,color,border}
        const due = renderDueDate(rowInfo);
        const latest = rowInfo.data?.latestComment;

        return {
            status: {
                ...status,
                border: status.border ?? ""
            },
            name: rowInfo.data?.name ?? '',
            dueText: due,
            // Count of uploaded document files (excludes comment attachments) - shown next to the
            // subtask name for upload-only tasks. Files orphaned by a document that was removed
            // from the template are left out: the subtask calls them out separately, and counting
            // them would say the subtask holds more than it is asking for.
            fileCount: splitConfiguredFiles(
                rowInfo.data?.files?.filter(f => !f.commentId) ?? [],
                rowInfo.data?.defaultStep?.settings
            ).claimed.length,
            commentRow:
                latest && {
                    id: `TaskListStepLastComment${rowInfo.data.id}`,
                    snippet: latest.comment,
                    tooltip: {
                        text: latest.comment,
                        user: getUserFullName(latest.updatedByUser),
                        date:
                            latest.dateUpdated &&
                            format(new Date(latest.dateUpdated as Date), 'MM/dd/yyyy h:mm a'),
                    },
                },
        };
    };

    const taskListVisible = () => {
        // Hide the task list if there's no phase data (e.g., deleted template with no grant instance)
        if (!phase) return false;

        // Always show in template mode
        if (currentMode === "templates") return true;

        // Before data is loaded, don't hide (wait for load to complete)
        if (!hasLoaded) return true;

        // Show if there are any visible subtasks (filtered by permissions in load)
        if (stepCount > 0) return true;

        // Show if user can add subtasks (even if none exist yet)
        if (canAddSubtask()) return true;

        // Show if user has task-level View permission (allows viewing task summary)
        // For STEP workflows, this is checked without regard to task status
        if (checkPhasePermission(phase, app, PermissionType.View, false, grantData?.id)) {
            return true;
        }

        // No visible subtasks, can't add, and no task-level View - hide the task
        return false;
    };

    const showRequestResponseSummaryButton = () => {
        return app.UserIsPO || app.UserIsGMS || app.UserIsSuper;
    };

    const onClickShowRequestResponseSummary = () => {
        // Clicking the task title selects and opens it, the way DC's phase header does. Expand
        // only - a second click keeps the task open rather than toggling it shut under the
        // summary that just opened. The chevron is the way to close it.
        if (collapsed) {
            collapseDefaultApplied.current = true;
            setCollapsed(false);
        }

        gridRef.current?.instance().deselectAll();
        taskListActions.clearSelections();
        taskListActions.setSelectedStep(null);  // Explicitly clear step
        taskListActions.setSelectedPhase(phase);
        // The summary describes the current cycle; a prior one is reached through its subtasks.
        taskListActions.setSelectedSubmissionId(null);
        phaseSelected.current = true;

        taskListActions.registerForClear(() => {
            phaseSelected.current = false;
        });

        showPhaseRequestResponseSummary?.(phase);
        onTaskSelected?.();
    };

    // Memoize the actual className string - pass it directly as a value, not a function
    const taskListClassName = useMemo(() => {
        // Highlight this phase only if:
        // 1. This phase is the one selected in context
        // 2. AND no step is currently selected (phase summary is shown, not a step)
        const isThisPhaseSelected = contextSelectedPhase &&
                                     phase &&
                                     contextSelectedPhase.id === phase.id &&
                                     !contextSelectedStep;

        return isThisPhaseSelected
            ? 'task-list-phase-selected'
            : 'task-list-phase';
    }, [contextSelectedPhase, phase, contextSelectedStep]);

    const canAddSubtask = useCallback(() => {
        if (!phase) return false;

        // Use config-based actions from backend
        // Backend already checks role/status/config in SetAvailablePhaseActions()
        return phase.phaseActions?.includes(PhaseAction.ADD) ?? false;
    }, [phase]);

    // Notify parent when visibility changes (after loading completes)
    useEffect(() => {
        if (hasLoaded && onVisibilityChange) {
            onVisibilityChange(taskListVisible());
        }
    }, [hasLoaded, phase, stepCount, currentMode, canAddSubtask, onVisibilityChange]);

    const addButtonVisible = useMemo(() => {
        // Use ref value for immediate updates
        const currentCount = stepCountRef.current;

        if (currentMode == "templates")
            return true;
        else {
            // Check permission to add subtasks (backend checks role/status/config)
            if (!canAddSubtask()) {
                return false;
            }

            const shouldShow = phaseSettings?.stepLimit != null ? phaseSettings.stepLimit > currentCount : true;
            return shouldShow;
        }
    }, [currentMode, canAddSubtask, phaseSettings?.stepLimit, stepCount]);

    // A core task is seeded and the server refuses to delete it, so the action is not offered -
    // the same rule the subtask delete already follows (TemplateTasksMain). Defaults to shown while
    // the phase is still loading, so a task that is not core never mounts without its button.
    const deleteButtonVisible = () => {
        if (currentMode !== "templates") return false;
        return phase?.defaultPhase?.settings?.isCore !== true;
    };

    const hideStatusIcons = () => {
        return tasklistMode === "templates";
    }

    // Per-step status is hidden for templates, where no subtask holds a real one. A document
    // folder does: it is In Progress until the documents it names are all uploaded, and the ones
    // that name none take their completion from the task.
    const hideStepStatus = () => {
        return tasklistMode === "templates";
    }

    // Group header text for the grid, by cycle number (0 = the original).
    const cycleGroupLabel = useCallback((cycle: number) => cycleVocabulary.cycleLabel(cycle), []);

    // Cycle number for a row, read off the phase so the grid can group and order by it.
    const cycleNumberOf = useCallback((step: StepDto) =>
        cyclesNewestFirst(phaseRef.current).find(c => c.id === step.phaseSubmissionId)?.resubmission ?? 0, []);

    // After a new cycle starts from the toolbar menu: reload, so the new cycle heads the grid.
    const onCycleStarted = useCallback(async () => {
        await taskListActions.refreshSteps();
    }, [taskListActions]);

    // Repeat / Start Resubmission as a toolbar menu entry beside Add and Delete, where this
    // toolbar is the task header (DC hosts the same action as an icon in its phase header).
    // The shared controller decides whether it is offered - template opt-in plus the cycle
    // permission - and runs the confirm and create.
    const cycle = useTaskCycleControlController({ phase, grantId: grantData?.id, onResubmitted: onCycleStarted });
    const repeatButtonVisible = currentMode !== "templates" && cycle.canStartNewCycle;
    const repeatButtonOptions = useMemo(() => ({
        icon: 'refresh',
        hint: cycle.startActionLabel,
        text: cycle.startActionLabel,
        disabled: cycle.busy,
        onClick: () => { void cycle.onClickStartCycle(); },
    }), [cycle.startActionLabel, cycle.busy, cycle.onClickStartCycle]);

    // Download All as a toolbar menu entry, where the template asks for it.
    const downloadAllVisible = currentMode !== "templates" && showsDownloadAll(phase);
    const phaseId = phase?.id;
    const moduleTypeId = phase?.defaultPhase?.moduleTypeId;
    const downloadAllOptions = useMemo(() => ({
        icon: 'download',
        hint: 'Download all files for this task',
        text: 'Download All Files',
        onClick: () => { if (phaseId && moduleTypeId != null) void downloadPhaseFiles(app, phaseId, moduleTypeId as Modules); },
    }), [app, phaseId, moduleTypeId]);

    return {
        // state/refs
        gridRef,
        phase,
        visibleSteps,
        currentCycleSteps,
        hasLoaded,

        // data
        dataSource,
        stepCount,

        // derived
        taskListVisible,
        showRequestResponseSummaryButton,
        addButtonVisible,
        deleteButtonVisible,
        taskListClassName,
        hideStepStatus,

        // options
        addButtonOptions,
        deleteButtonOptions,

        // handlers
        onSelectionChange,
        rowPrepared,
        onContentReady,
        onTooltipHiding,
        onClickShowRequestResponseSummary,

        // for row template render in view
        dataRowTemplate,

        hideStatusIcons,
        mode: currentMode,
        flags,
        grantData,
        nameShownExternally,

        collapsed,
        toggleCollapsed,

        groupedByCycle,
        cycleGroupLabel,
        cycleNumberOf,
        repeatButtonVisible,
        repeatButtonOptions,
        downloadAllVisible,
        downloadAllOptions,
        onCycleStarted
    };
}
